import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  approveGuestTabletDevice,
  createGuestTabletWorkstation,
  deactivateGuestTabletWorkstation,
  deleteGuestTabletDevice,
  fetchGuestTabletWorkstations,
  revokeGuestTabletDevice,
  updateGuestTabletDeviceLabel,
  updateGuestTabletWorkstation,
} from "@/lib/guestTabletApi";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { formatDateTimeShort } from "@/lib/bookingDates";
import type { GuestTabletDevice, GuestTabletWorkstation } from "@/types/guestTablet";

function formatDeviceTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatDateTimeShort(iso);
}

function deviceStatusLabel(status: string): string {
  if (status === "approved") return "Freigeschaltet";
  if (status === "revoked") return "Gesperrt";
  return "Wartet auf Freigabe";
}

function deviceStatusClass(status: string): string {
  if (status === "approved") {
    return "bg-[color-mix(in_srgb,var(--eh-green)_18%,transparent)] text-status-green";
  }
  if (status === "revoked") {
    return "bg-[color-mix(in_srgb,var(--eh-red)_18%,transparent)] text-status-red";
  }
  return "bg-[color-mix(in_srgb,var(--eh-orange)_18%,transparent)] text-status-orange";
}

function slugifyCode(label: string, index: number): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base ? `${base}-${index}` : `tablet-${index}`;
}

export function GuestTabletWorkstationsPanel() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [workstations, setWorkstations] = useState<GuestTabletWorkstation[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchGuestTabletWorkstations();
      setWorkstations(rows.filter((w) => w.is_active).sort((a, b) => a.sort_order - b.sort_order));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveLabel(ws: GuestTabletWorkstation, label: string) {
    if (!canEdit || label.trim() === ws.label) return;
    setSavingId(ws.id);
    setError(null);
    try {
      await updateGuestTabletWorkstation(ws.id, { label: label.trim() });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  async function addWorkstation() {
    if (!canEdit) return;
    const label = newLabel.trim();
    if (!label) {
      setError("Bitte einen Namen für das Tablet eingeben.");
      return;
    }
    setSavingId("new");
    setError(null);
    try {
      const code = slugifyCode(label, workstations.length + 1);
      await createGuestTabletWorkstation({
        code,
        label,
        sort_order: (workstations.length + 1) * 10,
      });
      setNewLabel("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Tablet konnte nicht angelegt werden");
    } finally {
      setSavingId(null);
    }
  }

  async function handleDeviceAction(
    action: () => Promise<unknown>,
    deviceId: string,
  ) {
    setSavingId(deviceId);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aktion fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  async function saveDeviceLabel(device: GuestTabletDevice, label: string) {
    if (!canEdit || label.trim() === (device.label ?? "")) return;
    await handleDeviceAction(
      () => updateGuestTabletDeviceLabel(device.id, label.trim() || null),
      device.id,
    );
  }

  async function removeWorkstation(ws: GuestTabletWorkstation) {
    if (!canEdit) return;
    if (!window.confirm(`Tablet „${ws.label}“ wirklich entfernen?`)) return;
    setSavingId(ws.id);
    setError(null);
    try {
      await deactivateGuestTabletWorkstation(ws.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Entfernen fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Tablets werden geladen…</p>;
  }

  return (
    <div className="space-y-6">
      {!canEdit ? <StammdatenWriteHint /> : null}

      <Card title="Gäste-Tablets an der Rezeption">
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Legen Sie fest, wie viele Gäste-Tablets Sie betreiben und wie sie heißen. Jeder
            Arbeitsplatz hat eine eigene URL, z. B.{" "}
            <code className="rounded bg-fill px-1 text-xs">/guest-tablet/reception-1</code>.
            Beim ersten Öffnen der URL muss das physische Gerät hier freigeschaltet werden – erst
            dann werden Gästedaten auf dem Tablet angezeigt. Mitarbeiter wählen am Login oder im
            Dashboard das passende Tablet – oder „Kein Tablet“.
          </p>

          {workstations.length > 0 ? (
            <ul className="space-y-3">
              {workstations.map((ws) => {
                const devices = ws.devices ?? [];
                const pendingCount = devices.filter((device) => device.status === "pending").length;
                return (
                  <li
                    key={ws.id}
                    className="overflow-hidden rounded-2xl border border-separator bg-card"
                  >
                    <div className="flex flex-col gap-3 p-4 md:flex-row md:items-end">
                      <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-xs font-medium text-label-secondary">
                        Anzeigename
                        <input
                          className="eh-input font-normal text-label"
                          disabled={!canEdit || savingId === ws.id}
                          defaultValue={ws.label}
                          onBlur={(e) => void saveLabel(ws, e.target.value)}
                        />
                      </label>
                      <div className="flex w-full min-w-0 flex-col gap-1.5 text-xs font-medium text-label-secondary md:w-44">
                        <span>Code (URL)</span>
                        <code className="flex h-10 items-center truncate rounded-lg border border-separator bg-fill px-3 font-mono text-xs font-normal text-label">
                          {ws.code}
                        </code>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <a
                          href={`/guest-tablet/${encodeURIComponent(ws.code)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-card px-3 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary"
                        >
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                          Öffnen
                        </a>
                        {canEdit ? (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={savingId === ws.id}
                            onClick={() => void removeWorkstation(ws)}
                          >
                            Entfernen
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    <div className="border-t border-separator bg-fill-quaternary px-4 py-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-medium text-label-secondary">
                          Registrierte Geräte
                        </p>
                        {pendingCount > 0 ? (
                          <span className="rounded-full bg-[color-mix(in_srgb,var(--eh-orange)_18%,transparent)] px-2.5 py-0.5 text-xs font-medium text-status-orange">
                            {pendingCount === 1 ? "1 wartet" : `${pendingCount} warten`}
                          </span>
                        ) : null}
                      </div>
                      {devices.length > 0 ? (
                        <ul className="space-y-2">
                          {devices.map((device) => (
                            <li
                              key={device.id}
                              className={`rounded-xl border bg-card p-3 text-sm ${
                                device.status === "pending"
                                  ? "border-[color-mix(in_srgb,var(--eh-orange)_45%,transparent)]"
                                  : "border-separator"
                              }`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                  <code className="truncate font-mono text-sm font-medium text-label">
                                    {device.device_token_short}
                                  </code>
                                  <span
                                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${deviceStatusClass(device.status)}`}
                                  >
                                    {deviceStatusLabel(device.status)}
                                  </span>
                                </div>
                                {canEdit ? (
                                  <div className="flex flex-wrap gap-2">
                                    {device.status === "pending" ? (
                                      <>
                                        <Button
                                          type="button"
                                          size="sm"
                                          disabled={savingId === device.id}
                                          onClick={() =>
                                            void handleDeviceAction(
                                              () => approveGuestTabletDevice(device.id),
                                              device.id,
                                            )
                                          }
                                        >
                                          Freischalten
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          disabled={savingId === device.id}
                                          onClick={() => {
                                            if (
                                              !window.confirm(
                                                "Registrierung dieses Geräts verwerfen?",
                                              )
                                            ) {
                                              return;
                                            }
                                            void handleDeviceAction(
                                              () => deleteGuestTabletDevice(device.id),
                                              device.id,
                                            );
                                          }}
                                        >
                                          Verwerfen
                                        </Button>
                                      </>
                                    ) : null}
                                    {device.status === "approved" ? (
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        disabled={savingId === device.id}
                                        onClick={() => {
                                          if (
                                            !window.confirm(
                                              "Gerät sperren? Es kann danach keine Gästedaten mehr anzeigen.",
                                            )
                                          ) {
                                            return;
                                          }
                                          void handleDeviceAction(
                                            () => revokeGuestTabletDevice(device.id),
                                            device.id,
                                          );
                                        }}
                                      >
                                        Sperren
                                      </Button>
                                    ) : null}
                                    {device.status === "revoked" ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        disabled={savingId === device.id}
                                        onClick={() =>
                                          void handleDeviceAction(
                                            () => approveGuestTabletDevice(device.id),
                                            device.id,
                                          )
                                        }
                                      >
                                        Erneut freischalten
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                              {canEdit ? (
                                <label className="mt-3 flex flex-col gap-1.5 text-xs font-medium text-label-secondary">
                                  Bezeichnung (optional)
                                  <input
                                    className="eh-input font-normal text-label"
                                    placeholder="z. B. iPad Rezeption links"
                                    defaultValue={device.label ?? ""}
                                    disabled={savingId === device.id}
                                    onBlur={(e) => void saveDeviceLabel(device, e.target.value)}
                                  />
                                </label>
                              ) : device.label ? (
                                <p className="mt-2 text-xs text-label-secondary">{device.label}</p>
                              ) : null}
                              <p className="mt-2 text-xs text-label-tertiary">
                                Zuletzt gesehen: {formatDeviceTime(device.last_seen_at)}
                                {device.approved_at
                                  ? ` · Freigeschaltet: ${formatDeviceTime(device.approved_at)}`
                                  : ""}
                              </p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="rounded-xl border border-dashed border-separator px-3 py-3 text-xs leading-relaxed text-label-tertiary">
                          Noch keine Geräte registriert. Öffnen Sie die Tablet-URL auf dem Gerät –
                          es erscheint hier zur Freischaltung.
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="rounded-xl border border-dashed border-separator px-4 py-6 text-center text-sm text-label-tertiary">
              Noch keine Tablets konfiguriert.
            </p>
          )}

          {canEdit ? (
            <div className="flex flex-col gap-3 border-t border-separator pt-4 sm:flex-row sm:items-end">
              <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-xs font-medium text-label-secondary">
                Neues Tablet
                <input
                  className="eh-input font-normal text-label"
                  placeholder="z. B. Rezeption links"
                  value={newLabel}
                  disabled={savingId === "new"}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addWorkstation();
                  }}
                />
              </label>
              <Button
                type="button"
                className="shrink-0"
                disabled={savingId === "new"}
                onClick={() => void addWorkstation()}
              >
                Tablet hinzufügen
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
